<template>
  <DSFormField
    :id="id"
    :name="props.name"
    :label="props.label"
    :inline-edit="props.inlineEdit"
    :tooltip="props.tooltip"
    :hidden-label="props.hiddenLabel"
    :required="props.required"
    :disabled="props.disabled"
    :error-messages="dsFormField.errorMessages.value"
  >
    <DSTextInput
      :id="id"
      ref="textInputRef"
      v-model="modelValue"
      :type="props.type"
      :name="props.name"
      :placeholder="props.placeholder"
      :inline-edit="props.inlineEdit"
      :disabled="props.disabled"
      :readonly="props.readonly"
      :required="props.required"
      :autocomplete="props.autocomplete"
      :maxlength="props.maxlength"
      :minlength="props.minlength"
      :suffix="props.suffix"
      :has-error="!!dsFormField.errorMessages.value.length"
      :hidden-input="props.hiddenInput"
      :prefix="props.prefix"
      :clearable="props.clearable"
      :aria-label="props.ariaLabel"
      @focus="onFocus"
      @blur="onBlur"
      @click:suffix-action="onSuffixAction"
      @update:model-value="dsFormField.clearFieldErrors"
    >
      <template
        v-if="slots.suffix"
        #suffix
      >
        <slot name="suffix" />
      </template>
    </DSTextInput>
  </DSFormField>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import DSFormField from "@/src/design_system/form_elements/form_field/components/DSFormField.vue";
import DSTextInput from "@/src/design_system/form_elements/text_input/components/DSTextInput.vue";
import type {
  DSTextFieldEmits,
  DSTextFieldModelValue,
  DSTextFieldProps,
  DSTextFieldSlots,
} from "../types/dsTextFieldTypes";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import { useDSFormField } from "@/src/design_system/form_elements/shared/composables/useDSFormField";
import { useDSFormFieldId } from "@/src/design_system/form_elements/shared/composables/useDSFormFieldId";

const props = withDefaults(defineProps<DSTextFieldProps>(), {
  hiddenLabel: false,
  inlineEdit: false,
  clearable: true,
  disabled: false,
  readonly: false,
  required: false,
  tooltip: undefined,
  errorMessages: undefined,
  rules: undefined,
});

const modelValue = defineModel<DSTextFieldModelValue>({ default: "" });

const emit = defineEmits<DSTextFieldEmits>();
const slots = defineSlots<DSTextFieldSlots>();

const textInputRef = ref<InstanceType<typeof DSTextInput>>();

const { focus, blur } = useDSInput(textInputRef);

const dsFormField = useDSFormField<DSTextFieldModelValue>({
  name: computed(() => props.name),
  errorMessages: computed(() => props.errorMessages),
  value: modelValue,
  rules: computed(() => props.rules),
});

const { id } = useDSFormFieldId({ props });

const onFocus = (event: FocusEvent) => {
  emit("focus", event);
};

const onBlur = (event: FocusEvent) => {
  emit("blur", event);
};

const onSuffixAction = (event: MouseEvent) => {
  emit("click:suffix-action", event);
};

defineExpose({
  focus,
  blur,
});
</script>
